import React, { useEffect, useRef, useState } from 'react';

type LazyImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  /** Scroll container; visibility is measured against this element, not the viewport. */
  scrollRoot?: Element | null;
  /** When true, do not observe or fetch until `scrollRoot` is set (avoids viewport false-positives). */
  waitForScrollRoot?: boolean;
  /** Preload images slightly before they enter the scroll area. */
  rootMargin?: string;
};

/**
 * Defers setting `src` until the image is near the visible scroll area.
 * Avoids hundreds of simultaneous fetches when a long thumbnail list mounts.
 */
function LazyImage({
  src,
  alt,
  scrollRoot = null,
  waitForScrollRoot = false,
  rootMargin = '100px 0px',
  className,
  ...rest
}: LazyImageProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [loadedSrc, setLoadedSrc] = useState<string | undefined>();

  useEffect(() => {
    setLoadedSrc(undefined);
  }, [src]);

  useEffect(() => {
    const el = imgRef.current;
    if (!el || !src) {
      return undefined;
    }

    if (waitForScrollRoot && !scrollRoot) {
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setLoadedSrc(src);
      return undefined;
    }

    let cancelled = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !cancelled) {
          setLoadedSrc(src);
          observer.disconnect();
        }
      },
      { root: scrollRoot, rootMargin, threshold: 0.01 },
    );

    observer.observe(el);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [src, scrollRoot, waitForScrollRoot, rootMargin]);

  const classNames = [
    className,
    loadedSrc ? 'lazy-image--loaded' : 'lazy-image--pending',
  ].filter(Boolean).join(' ');

  return (
    <img
      ref={imgRef}
      src={loadedSrc}
      alt={alt}
      className={classNames}
      decoding="async"
      {...rest}
    />
  );
}

export default LazyImage;
